import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const NetworkContext = createContext({ isOnline: true, isReconnecting: false });

export const useNetwork = () => useContext(NetworkContext);

// Global setter so axios interceptors can trigger offline state
let _setOfflineFromInterceptor = null;
export const triggerOfflineFromInterceptor = () => {
  _setOfflineFromInterceptor?.();
};

const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectTimerRef = useRef(null);

  const goOffline = useCallback(() => {
    setIsOnline(false);
    setIsReconnecting(false);
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  }, []);

  const goOnline = useCallback(() => {
    setIsReconnecting(true);
    // Brief reconnecting state before fully restoring
    reconnectTimerRef.current = setTimeout(() => {
      setIsOnline(true);
      setIsReconnecting(false);
    }, 800);
  }, []);

  const retry = useCallback(() => {
    setIsReconnecting(true);
    // Check actual connectivity
    reconnectTimerRef.current = setTimeout(() => {
      if (navigator.onLine) {
        setIsOnline(true);
        setIsReconnecting(false);
      } else {
        setIsReconnecting(false);
      }
    }, 1500);
  }, []);

  useEffect(() => {
    _setOfflineFromInterceptor = goOffline;
    return () => { _setOfflineFromInterceptor = null; };
  }, [goOffline]);

  useEffect(() => {
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [goOnline, goOffline]);

  return (
    <NetworkContext.Provider value={{ isOnline, isReconnecting, retry }}>
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkProvider;
